/**
 * California statewide LDV VMT from ghg_inventory_precalculated (State row).
 * After state sync, ldv_vmt matches the sum of CA county rows — same basis as GHG inventory charts.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export const CALIFORNIA_STATE_GEOFIPS = 6;

/** True when the selected place is California statewide inventory (geofips 6 or common state names). */
export function isCaliforniaStateInventoryLocation(
  locationName: string | null | undefined,
  geofips?: number | null
): boolean {
  if (geofips != null && Number(geofips) === CALIFORNIA_STATE_GEOFIPS) return true;
  const n = (locationName || '').trim().toLowerCase();
  return (
    n === 'california' ||
    n === 'state of california' ||
    n === 'ca' ||
    n === 'calif.'
  );
}

export interface CaliforniaStateLdvVmtRow {
  year: number;
  ldv_vmt: number;
}

/**
 * Paginated fetch of State geography_type rows for California (geofips 6).
 */
export async function fetchCaliforniaStateLdvVmtSeries(
  client: SupabaseClient
): Promise<CaliforniaStateLdvVmtRow[]> {
  const rows: CaliforniaStateLdvVmtRow[] = [];
  const pageSize = 1000;
  let from = 0;

  for (;;) {
    const { data, error } = await client
      .from('ghg_inventory_precalculated')
      .select('year, ldv_vmt')
      .eq('geofips', CALIFORNIA_STATE_GEOFIPS)
      .eq('geography_type', 'State')
      .order('year', { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) {
      return [];
    }

    const batch = data ?? [];
    if (batch.length === 0) break;

    for (const r of batch) {
      const y = Number(r.year);
      const v = r.ldv_vmt == null ? NaN : Number(r.ldv_vmt);
      if (Number.isFinite(y) && Number.isFinite(v)) {
        rows.push({ year: y, ldv_vmt: v });
      }
    }

    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return rows;
}
